import React, { useState, useEffect, useCallback, useContext } from "react";
import SocketContext from "../../../../context/SocketContext";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Dark } from "../../../../Theme/Theme";
import Loading from "react-loading";

const backend = await new Backend().init();

export default function SqButton({ symbol_id, symbolType }) {
  const navigation = useNavigate();
  const socket = useContext(SocketContext);

  const [standing, setStanding] = useState(null);
  const [bid, setBid] = useState(0);
  const [ask, setAsk] = useState(0);
  const [profitLoss, setProfitLoss] = useState(0);
  const [color, setColor] = useState("bg-[var(--neutral)] text-[var(--text)]");
  const [loading, setLoading] = useState(false);

  // ✅ Load standing details
  const loadStanding = useCallback(async () => {
    if (!symbol_id) return;
    setLoading(true);
    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        symbol_id,
      };

      const r = await backend.symbol_standing(data);
      if (r.error === "False") {
        if (
          !r.standing ||
          (Array.isArray(r.standing) && r.standing.length === 0)
        ) {
          setStanding(null);
        } else {
          setStanding(r.standing);
        }
      } else {
        toast.error(r.message || "Failed to load standing");
      }
    } catch (err) {
      console.error("Load standing failed:", err);
      toast.error("Error loading standing");
    } finally {
      setLoading(false);
    }
  }, [symbol_id]);

  useEffect(() => {
    loadStanding();
  }, [loadStanding]);

  // ✅ Profit/Loss calculation
  const calculateProfitLoss = useCallback(
    (currentBid, currentAsk, currentLTP) => {
      if (!standing) return;

      let buy_sell = standing[0].buy_sell;
      let qty = standing[0].qty;
      let rate = standing[0].rate;
      let type = standing[0].type;
      let dollar_rate = standing[0].dollar_rate || 1;

      let calcPrice = 0;
      let newPL = 0;

      if (buy_sell === 0) {
        calcPrice = currentBid || currentLTP;
        if (calcPrice)
          newPL = (parseFloat(calcPrice) - parseFloat(rate)) * parseFloat(qty);
      } else {
        calcPrice = currentAsk || currentLTP;
        if (calcPrice)
          newPL = (parseFloat(rate) - parseFloat(calcPrice)) * parseFloat(qty);
      }

      if (standing[0].rupee_dollar === 0) {
        if (
          [
            "comex_future",
            "crypto_future",
            "forex_future",
            "us_stock",
            "index",
            "dgcx",
          ].includes(type)
        ) {
          newPL *= parseFloat(dollar_rate || 1);
        }
      }

      setProfitLoss(newPL);
      setColor(
        newPL > 0
          ? "bg-[var(--buy)] text-[var(--white)]"
          : "bg-[var(--sell)] text-[var(--white)]",
      );
    },
    [standing],
  );

  // ✅ Handle socket trade update
  const socketTrade = useCallback(
    (msg) => {
      if (!msg) return;
      const { Bid, Ask, LTP } = msg;

      if (Bid !== bid) setBid(Bid);
      if (Ask !== ask) setAsk(Ask);
      calculateProfitLoss(Bid, Ask, LTP);
    },
    [bid, ask, calculateProfitLoss],
  );

  // ✅ Setup socket listener
  useEffect(() => {
    if (!socket || !symbol_id) return;
    socket.emit("giverate", symbol_id);
    socket.on("trade" + symbol_id, socketTrade);
    return () => socket.off("trade" + symbol_id, socketTrade);
  }, [socket, symbol_id, socketTrade]);

  // ✅ Recalculate P/L after standing is loaded or prices change
  useEffect(() => {
    if (standing && (bid || ask)) {
      const mid = (parseFloat(bid) + parseFloat(ask)) / 2 || 0;
      calculateProfitLoss(bid, ask, mid);
    }
  }, [standing, bid, ask, calculateProfitLoss]);

  // ✅ Square-off logic
  const handleSqStanding = useCallback(async () => {
    if (!standing) return;
    setLoading(true);
    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id: localStorage.getItem("id"),
        standing_id: standing[0].id,
      };

      const r = await backend.sq_single(data);
      if (r.error === "False") {
        toast.success(r.message || "Position squared off successfully");
        loadStanding();
      } else {
        toast.error(r.message || "Failed to square off");
      }
    } catch (err) {
      console.error("Square off failed:", err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [standing, navigation]);

  // ✅ Hide button if standing is empty/null
  if (
    !standing ||
    standing === 0 ||
    (Array.isArray(standing) && standing.length === 0)
  ) {
    return null;
  }

  // ✅ Extract qty
  const type = standing?.[0]?.type || 0;
  const qty =
    type === "nse" ||
    type === "nse_cash" ||
    type === "us_stock" ||
    type === "sgx" ||
    type === "index"
      ? standing?.[0]?.qty || 0
      : standing?.[0]?.qty / standing?.[0]?.lot_size;

  const Label =
    type === "nse" ||
    type === "nse_cash" ||
    type === "us_stock" ||
    type === "sgx" ||
    type === "index"
      ? "QTY"
      : "LOT";

  const buttonLabel = profitLoss > 0 ? "CLOSE WITH PROFIT" : "CLOSE WITH LOSS";

  const sign = standing?.[0]?.rupee_dollar === 0 ? "" : "$"

  return (
    <div className="cancelContainer">
      <button
        onClick={handleSqStanding}
        disabled={loading}
        className={`btnsqcancel small ${color} `}
      >
        {loading ? (
          <Loading type="spin" color={Dark.text} height={20} width={20} />
        ) : (
          <div>
            <div>{buttonLabel}</div>
            <div>
              {`${qty} ${Label} @ ${profitLoss.toFixed(2)}${sign}`}
            </div>
          </div>
        )}
      </button>
    </div>
  );
}
